import * as Styled from './Bookmarks.styles'
import * as Type from 'redux/actions/types'
import React, { useContext, useEffect, useState } from 'react'
import { useLocation, Link as RouterLink } from 'react-router-dom'
import { TextContext, ErrorBoundary } from 'hocs'
import { useDispatch, useSelector } from 'react-redux'
import {
  BOOKABLE_TYPES,
  LATITUDE_PATTERN,
  LONGITUDE_PATTERN,
  mappedEntryPointNames,
  SHOW_SINGLE_QSR_INDICATORS,
  GOOGLE_API_KEY,
} from 'settings/app/constants'
import {
  fetchBookmarkDetails,
  toggleBookmark,
  clearBookmarkDetails,
  removeBookmarkFromSelectedDetails,
} from 'redux/actions/bookmarkActions'
import {
  Link,
  Grid,
  Loader,
  PrintButton,
  CopyToClipboardButton,
  Icon,
} from 'components/atoms'
import {
  Map,
  MessageBox,
  NavigationDropDown,
  NavigationTabs,
  ResultTile,
} from 'components/molecules'
import { useMobileSwitch } from 'hooks'
import {
  getNumberOfBookmarks,
  getReplacedString,
  getViewHeadline,
} from 'settings/app/helpers'
import {
  buildUrl,
  getBookmarkShareUrl,
  getComparePath,
  getDetailURL,
  getLocationParams,
  getSearchGetParams,
} from 'settings/app/urlHelpers'
import { getHeaderHeight } from 'settings/app/uiHelpers'
import * as Component from 'components/organisms/SearchHeader/SearchHeader.styles'
import { useHistory } from 'react-router'

/**
 * functional container for all detail views
 * @returns {JSX.Element}
 * @constructor
 */
const Bookmarks = () => {
  const dispatch = useDispatch()
  const textContext = useContext(TextContext)
  const history = useHistory()
  const location = useLocation()
  const isMobile = useMobileSwitch()
  const state = useSelector((state) => state)

  // local state
  const { apiError, bookmarks, filter, search } = state
  const { selectedDetails, types: bookmarkStates } = bookmarks
  const { activeSort: currentSort } = filter
  const { prevParams, qsrIdentifier, params } = search
  const [{ selectedType, sourceType }, setBookmarkView] = useState({}) // selectedType && sourceType
  const [isMapFullscreen, setIsMapFullscreen] = useState(false)
  const [hoveredItemId, setHoveredItemId] = useState(null)
  const shouldFetch = React.useRef(true)

  /**
   * set selectedTyp from url or local state
   * set bookmarkTyp for rendering/xhr
   */
  useEffect(() => {
    if (!selectedType) {
      const locationParams = getLocationParams(location.search)
      let nextSelectedTyp, sourceTyp

      // use url params if valid ||  use local state
      if (locationParams.valid) {
        // nextSelectedTyp = locationParams.type
        nextSelectedTyp = locationParams.resultType
        sourceTyp = Type.SOURCE_TYPE_URL
      } else {
        const { lastEntryPoint } = history.location.state

        /**
         * if => on initial load use link state
         * else => when on page refresh the initial bookmarks do not exists, use next booked elements
         */
        if (bookmarkStates[lastEntryPoint]?.items.length > 0) {
          nextSelectedTyp = lastEntryPoint
        } else {
          Object.keys(bookmarkStates).forEach((k) => {
            if (bookmarkStates[k].count > 0) {
              nextSelectedTyp = k
              return true
            }
          })
        }
        sourceTyp = Type.SOURCE_TYPE_LOCAL
      }

      setBookmarkView({
        selectedType: nextSelectedTyp,
        sourceType: sourceTyp,
      })
    }
  }, [location, bookmarkStates, history.location.state, selectedType])

  /**
   * get bookmark rendering data
   */
  useEffect(() => {
    if (!!selectedType && shouldFetch.current) {
      switch (sourceType) {
        // shared bookmark list
        case Type.SOURCE_TYPE_URL:
          const locationParams = getLocationParams(location.search)
          dispatch(clearBookmarkDetails())
          dispatch(
            fetchBookmarkDetails(
              locationParams.resultType,
              locationParams.ids,
              {
                ...getSearchGetParams({
                  resultType: selectedType,
                  prevParams: locationParams,
                }),
              }
            )
          )
          shouldFetch.current = false
          break

        // normal view (search => add => bookmark view)
        case Type.SOURCE_TYPE_LOCAL:
          // otherwise fetch new results
          dispatch(clearBookmarkDetails())
          dispatch(
            fetchBookmarkDetails(
              selectedType,
              bookmarkStates[selectedType].items,
              {
                ...getSearchGetParams({
                  resultType: selectedType,
                  prevParams: bookmarkStates[selectedType].searchParams,
                }),
              }
            )
          )
          shouldFetch.current = false
          break
      }
    }
  }, [selectedType, bookmarkStates, sourceType, location, dispatch])

  /**
   * bookmark remove handler
   * @param id
   * @param type
   */
  const onToggleBookmarkHandler = (id, type) => {
    dispatch(
      toggleBookmark({
        id,
        type,
        searchParams: bookmarkStates[selectedType].searchParams,
      })
    )
    dispatch(removeBookmarkFromSelectedDetails(id)) // remove bookmark from selectedDetails (result data @bookmark state)

    // change selected typ when removed bookmark is deleted
    if (bookmarkStates[selectedType].items.length === 1) {
      const nextSelectedTyp = Object.keys(bookmarkStates).find(
        (type) => type !== selectedType && bookmarkStates[type].count > 0
      )
      setBookmarkView((prevParams) => ({
        ...prevParams,
        selectedType: nextSelectedTyp,
      }))
      shouldFetch.current = true
    } else {
      shouldFetch.current = false
    }
  }

  const navTabSelectHandler = (id) => {
    setBookmarkView((prevParams) => ({
      ...prevParams,
      selectedType: id,
    }))
    shouldFetch.current = true
  }

  const toggleMapFullscreenHandler = (bool) => {
    setIsMapFullscreen(bool)
    if (!bool) setHoveredItemId(null)
  }

  /**
   * get tab items array of abjects
   * id => entryPoint (doctors, hosp...)
   * label => mapped EntryPoint with number of bocked elements
   *
   * @returns {*[]}
   */
  const getTabItems = () => {
    const tabItemArray = []

    // if data from url return only tab-nav for current bookmark-type
    // show only tab-nav when more then one result type is at bookmark list
    if (sourceType === Type.SOURCE_TYPE_URL) return tabItemArray

    if (getNumberOfBookmarks({ bookmarkState: bookmarkStates }) > 0) {
      Object.keys(bookmarkStates).forEach((key) => {
        if (bookmarkStates[key].count === 0) return true

        tabItemArray.push({
          id: key,
          label: `${ textContext[`bookmark.title.${ key }`] } (${
            bookmarkStates[key].count
          })`,
        })
      })
    }

    return tabItemArray
  }

  const getMapMarkers = () => {
    if (!selectedDetails || !selectedDetails.results) return []

    // check if results has valid coordinates for map
    let numberOfMissingCoordinates = 0
    selectedDetails.results.forEach((item) => {
      if (
        !item.coordinates ||
        !LATITUDE_PATTERN.test(item.coordinates.lat) ||
        !LONGITUDE_PATTERN.test(item.coordinates.lon)
      ) {
        // eslint-disable-next-line no-plusplus
        numberOfMissingCoordinates++
      }
    })
    if (numberOfMissingCoordinates === selectedDetails.results.length) return []

    return selectedDetails.results.map((item) => ({
      lat: item.coordinates?.lat,
      lng: item.coordinates?.lon,
      title: item.locationName,
      street: item.address.street,
      zip: item.address.zip,
      city: item.address.city,
      phone: item.phone,
      id: item.id,
      active: item.id === hoveredItemId,
      detailUrl: item.id
        ? getDetailURL({
            id: item.id,
            entryPoint: selectedType,
            city: item.address.city,
            zip: item.address.zip,
            key: selectedDetails.key,
            type: selectedDetails.type,
          })
        : null,
    }))
  }

  const markerClickHandler = (index, id) => {
    if (!isMapFullscreen) {
      const itemEl = document.getElementById(`bookmark-item-${ id }`)
      const viewportWidth = Math.max(
        document.documentElement.clientWidth,
        window.innerWidth || 0
      )
      const typoHeaderHeight = getHeaderHeight(viewportWidth)

      itemEl.scrollIntoView()
      const offset = itemEl.getBoundingClientRect().top - typoHeaderHeight

      if (offset < 0) {
        window.scrollBy(0, offset)
      }
    }
  }

  /**
   * map map with error boundary and props
   * @returns {JSX.Element}
   */
  const getMap = () => (
    <ErrorBoundary>
      <div style={{ height: isMapFullscreen ? '570px' : '390px' }}>
        <Map
          id='map'
          data-cy='map'
          apiKey={ GOOGLE_API_KEY }
          defaultCenter={{ lat: 51.05, lng: 13.7 }}
          markers={ getMapMarkers() }
          onMarkerClicked={ markerClickHandler }
          onMarkerHovered={ (isHovered, index, id) => {
            setHoveredItemId(isHovered ? id : null)
          } }
          onToggleFullscreen={ (value) => toggleMapFullscreenHandler(value) }
          mapPosition='resultList'
          isMapFullscreen={ isMapFullscreen }
          height={ '100%' }
        />
      </div>
    </ErrorBoundary>
  )

  /**
   * get back path with all arguments
   * @returns {string}
   */
  const getBackPath = () => {
    const [_, getUrl] = buildUrl(state, params)
    return `/${ getUrl }`
  }

  /**
   * get compare path with all params (included searchKey and searchType)
   * @returns {string}
   */
  const buildBookmarkShareUrl = () =>
    getBookmarkShareUrl(
      selectedDetails,
      selectedType,
      bookmarkStates[selectedType].searchParams,
      currentSort
    )

  return (
    <Styled.BookmarksView>
      {/* header content */}
      <h1>{getViewHeadline({ selectedType, view: 'bookmark' })}</h1>

      <div
        className={ `link-header ${
          sourceType === Type.SOURCE_TYPE_URL ? 'right' : ''
        }` }>
        {sourceType !== Type.SOURCE_TYPE_URL && (
          <Link to={ getBackPath() } className='link back-link'>
            {textContext['navigation.backToOverview']}
          </Link>
        )}

        {selectedDetails?.results.length > 1 && (
          <Component.CompareButton
            text={ textContext['general.compare.Link'] }
            onClick={ () =>
              history.push(
                getComparePath(
                  selectedDetails,
                  selectedType,
                  sourceType,
                  bookmarkStates[selectedType].searchParams
                )
              )
            }
            icon={ <Icon className='icon-f-compare' /> }
            className='compare-button'
          />
        )}
      </div>

      {/* show hint if no items at list */}
      {!selectedType && (
        <div className={ 'container-content' }>
          <p className={ 'h4' }>{textContext['bookmark.noItems']}</p>
        </div>
      )}

      {/* if items available at bookmark list */}
      {selectedType && (
        <div data-cy='bookmark-container'>
          {isMobile ? (
            <NavigationDropDown
              items={ getTabItems() }
              selectedId={ selectedType }
              onSelect={ navTabSelectHandler }
              locked={ getTabItems().length < 2 }
            />
          ) : (
            <NavigationTabs
              data-cy='tabs-list'
              className={ `${ getTabItems().length === 1 ? 'full-size' : null }` }
              items={ getTabItems() }
              selectedId={ selectedType }
              onSelect={ navTabSelectHandler }
            />
          )}
          <Grid container spacing={ 6 }>
            {!isMobile && (
              <>
                <Grid
                  data-cy='bookmark-map'
                  item
                  xs={ 12 }
                  md={ isMapFullscreen ? 12 : 4 }>
                  {getMap()}
                </Grid>
                {isMapFullscreen && <Grid item xs={ 4 } />}
              </>
            )}
            <Grid data-cy='bookmark-results' item xs={ 12 } md={ 8 }>
              {apiError && <MessageBox isError={ true } showClose={ false } />}
              {!apiError && (
                <ErrorBoundary>
                  {!selectedDetails && (
                    <Loader variant='loading' data-cy='loading-indicator'>
                      {getReplacedString({
                        search:
                          textContext['searchMain.loadMore.LoadingMessage'],
                        replaceWith: mappedEntryPointNames[selectedType],
                      })}
                    </Loader>
                  )}

                  {selectedDetails && (
                    <>
                      {selectedDetails.results.map((result) => (
                        <ResultTile
                          id={ `bookmark-item-${ result.id }` }
                          className={ 'bookmark-item' }
                          data-cy='result-tile'
                          key={ result.id }
                          active={ result.id === hoveredItemId }
                          open={ false }
                          generalTooltips={
                            selectedDetails?.generalTooltips
                              ? selectedDetails.generalTooltips
                              : {}
                          }
                          setBooked={ () =>
                            onToggleBookmarkHandler(result.id, selectedType)
                          }
                          isBooked={
                            bookmarkStates[selectedType].items.indexOf(
                              result.id.toString()
                            ) > -1
                          }
                          isBookable={ BOOKABLE_TYPES.includes(selectedType) }
                          handleHover={ (isHovered) => {
                            setHoveredItemId(isHovered ? result.id : null)
                          } }
                          linkComponent={ RouterLink }
                          linkComponentProps={{
                            to: getDetailURL({
                              id: result.id,
                              entryPoint: selectedType,
                              city: null,
                              zip: null,
                              key: selectedDetails.key,
                              type: selectedDetails.type,
                            }),
                          }}
                          is_treatment_reason={
                            selectedDetails.is_treatment_reason
                          }
                          is_aop={ selectedDetails.is_aop }
                          type={ selectedType }
                          result={ result }
                          currentSort={
                            sourceType === Type.SOURCE_TYPE_URL
                              ? getLocationParams(location.search).currentSort
                              : currentSort
                          }
                          isMobile={ isMobile }
                          singleQSRIndicator={ [...SHOW_SINGLE_QSR_INDICATORS] }
                          qsrIdentifier={ [...qsrIdentifier] }
                          searchKey={ prevParams.key }
                        />
                      ))}
                      <div className={ 'bookmark-footer' }>
                        <CopyToClipboardButton
                          contentToCopy={ buildBookmarkShareUrl() }
                          data-cy='bookmark-share-btn'
                        />
                        <PrintButton
                          className={ 'bookmark-print' }
                          data-cy='bookmark-print-btn'
                        />
                        {selectedDetails.results.length > 1 && (
                          <Link
                            data-cy='bookmark-compare-btn'
                            isNativeLink={ false }
                            to={ getComparePath(
                              selectedDetails,
                              selectedType,
                              sourceType
                            ) }
                            className={ 'bookmark-compare btn btn-default' }>
                            {textContext['bookmark.button.compare']}
                          </Link>
                        )}
                      </div>
                    </>
                  )}
                </ErrorBoundary>
              )}
            </Grid>
          </Grid>
        </div>
      )}
    </Styled.BookmarksView>
  )
}

export default Bookmarks
